import type {
  DashboardJobEnd,
  DashboardSeniorFeedback,
  DashboardQualtricsForm,
} from '@/api/dashboardApi';

interface DashboardItem {
  id: string;
  type: 'jobEnd' | 'seniorFeedback' | 'qualtricsForm';
  title: string;
  dueDate: string;
  status: string;
  link?: string;
  isCollator?: boolean;
}

export interface ProjectGroup {
  projectName: string;
  items: DashboardItem[];
}

export function isDone(status: string): boolean {
  return status === 'Done';
}

export function groupByProject(
  jobEnds: DashboardJobEnd[],
  seniorFeedback: DashboardSeniorFeedback[],
  qualtricsForm: DashboardQualtricsForm[]
): ProjectGroup[] {
  const projectMap = new Map<string, DashboardItem[]>();

  jobEnds.forEach((je) => {
    const project = je.projectName || 'Other';
    if (!projectMap.has(project)) projectMap.set(project, []);
    projectMap.get(project)!.push({
      id: `je-${je.id}`,
      type: 'jobEnd',
      title: je.title,
      dueDate: je.dueDate,
      status:
        je.isOriginalCollator && je.delegationStatus === 'Pending'
          ? 'pendingDelegation'
          : je.isOriginalCollator && je.delegationStatus === 'Approved'
            ? (je.status === 'Done' ? je.status : 'delegated')
            : je.role === 'Delegate' && je.delegationStatus === 'Pending'
              ? 'delegationRequest'
              : je.status,
      link: `/job-ends/${je.id}`,
      isCollator: je.role.includes('Collator'),
    });
  });

  seniorFeedback.forEach((sf) => {
    const project = sf.projectName || 'Other';
    if (!projectMap.has(project)) projectMap.set(project, []);
    projectMap.get(project)!.push({
      id: `sf-${sf.id}`,
      type: 'seniorFeedback',
      title: `Senior Feedback - ${sf.appraiseeName}`,
      dueDate: sf.dueDate,
      status: sf.status,
      link: `/job-ends/${sf.jobEndId}`,
    });
  });

  qualtricsForm.forEach((qf) => {
    const project = qf.projectName || 'Other';
    if (!projectMap.has(project)) projectMap.set(project, []);
    projectMap.get(project)!.push({
      id: `qf-${qf.id}`,
      type: 'qualtricsForm',
      title: qf.title,
      dueDate: qf.dueDate,
      status: qf.status,
      link: qf.link,
    });
  });

  const groups: ProjectGroup[] = [];
  projectMap.forEach((items, projectName) => {
    groups.push({ projectName, items });
  });

  return groups.sort((a, b) => a.projectName.localeCompare(b.projectName));
}
